import {
  Component,
  OnChanges,
  OnInit,
  Optional,
  Self,
  SimpleChanges,
  Input,
} from '@angular/core';
import { NgControl, Validators } from '@angular/forms';
import {
  ControlsOf,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';

import {
  BillingFormElements,
  GeneratorModel,
} from '@digital-platform/generators/domain';
import {
  AddressInputFields,
  AddressModel,
  ContactModel,
} from '@digital-platform/shared/domain';
import { NestedFormComponent } from '@digital-platform/shared/ui';

type BillingInternalFormElement = ControlsOf<
  Omit<BillingFormElements, 'address'>
> & {
  address: FormControl<AddressInputFields>;
};

@Component({
  selector: 'digital-platform-generator-billing-form',
  templateUrl: './generator-billing-form.component.html',
  styleUrls: ['./generator-billing-form.component.scss'],
})
export class GeneratorBillingFormComponent
  extends NestedFormComponent<BillingFormElements, BillingInternalFormElement>
  implements OnInit, ControlValueAccessor<BillingFormElements>, OnChanges
{
  public form: FormGroup<BillingInternalFormElement>;
  @Input() profileStatus: string;
  @Input() isLegacy = false;
  @Input() tabIndex = 1;

  isAddressRequired = true;
  constructor(
    private formBuilder: FormBuilder,
    @Optional() @Self() public controlDir: NgControl
  ) {
    super(controlDir);
  }

  initializeForm(billing) {
    this.form = this.formBuilder.group({
      address: this.formBuilder.control({
        addressLine1: billing.address?.addressLine1,
        addressLine2: billing.address?.addressLine2,
        addressLine3: billing.address?.addressLine3,
        city: billing.address?.city,
        county: billing.address?.county,
        stateCode: billing.address?.stateCode,
        postalCode: billing.address?.postalCode,
        postalCodeSuffix: billing.address?.postalCodeSuffix,
        country: billing.address?.country || 'US',
      }),

      contact: this.formBuilder.group({
        name:
          this.isLegacy === false
            ? [billing.contact.name, [Validators.required]]
            : [billing.contact.name],
        phone:
          this.isLegacy === false
            ? [billing.contact.phone, [Validators.required]]
            : [billing.contact.phone],
        phoneExt: [billing.contact.phoneExt],
        fax: [billing.contact.fax],
        email:
          this.isLegacy === false
            ? [billing.contact.email, [Validators.required, Validators.email]]
            : [billing.contact.email],
      }),

      name:
        this.isLegacy === false
          ? [billing.name, [Validators.required]]
          : [billing.name],
      notes: [billing.notes],
    });
  }

  ngOnInit(): void {
    let billingStream: BillingFormElements = {
      ...GeneratorModel.DEFAULT_CONFIG.billing,
      address: {
        ...AddressModel.DEFAULT_CONFIG,
        addressLine2: '',
        addressLine3: '',
        city: '',
        stateCode: '',
        county: '',
        postalCodeSuffix: '',
        country: '',
      },
      contact: {
        ...ContactModel.DEFAULT_CONFIG,
        email: '',
        phoneExt: '',
        fax: '',
      },
    };

    if (this.value) {
      billingStream = this.value;
    }
    if (this.form) {
      this.form.patchValue(billingStream, {
        emitEvent: false,
      });
    } else {
      this.initializeForm(billingStream);
    }
    super.ngOnInit();
  }

  errorHandling = (control: string, error: string) => {
    if (
      this.form.controls[control].touched &&
      this.form.controls[control].hasError(error)
    ) {
      this.form.updateValueAndValidity();
      return true;
    }
    return false;
  };

  errorHandlingContact = (control: string, error: string) => {
    if (
      this.form.controls.contact.controls[control].touched &&
      this.form.controls.contact.controls[control].hasError(error)
    ) {
      this.form.updateValueAndValidity();
      return true;
    }
    return false;
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.profileStatus?.currentValue === 'Not Submitted') {
      this.form.controls.name.removeValidators(Validators.required);
      this.form.controls.contact.controls.name.removeValidators(
        Validators.required
      );
      this.form.controls.contact.controls.phone.removeValidators(
        Validators.required
      );
      this.form.controls.contact.controls.email.removeValidators(
        Validators.required
      );
      this.form.updateValueAndValidity();
      this.isAddressRequired = false;
    } else if (
      this.isLegacy === false &&
      changes.profileStatus?.currentValue &&
      changes.profileStatus?.currentValue !== 'Not Submitted'
    ) {
      if (this.form) {
        this.form.controls.name.addValidators(Validators.required);
        this.form.controls.contact.controls.name.addValidators(
          Validators.required
        );
        this.form.controls.contact.controls.phone.addValidators(
          Validators.required
        );
        this.form.controls.contact.controls.email.addValidators(
          Validators.required
        );

        this.form.updateValueAndValidity();
      }
      this.isAddressRequired = true;
    }
  }
}
